import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";
import type { Finding, FindingDetail } from "../api/types";
import { Confidence } from "../components/Badges";
import { EvidenceViewer, imageUrl } from "../components/EvidenceViewer";
import { useToast } from "../components/Feedback";
import { Field, PageHeader, Panel } from "../components/Layout";
import { EmptyState, ErrorState, QueryState, SkeletonBlock } from "../components/States";
import { chainage } from "../lib/format";
import { uniq } from "../lib/useUrlState";

interface Catalogue { defects: { defect_code: string; defect_name: string; category: string; measurement_unit: string | null }[]; severities: string[] }

function Decision({ detail, catalogue, onDone }: { detail: FindingDetail; catalogue: Catalogue | undefined; onDone: (id: string, action: string) => void }) {
  const f = detail.finding;
  const isDefect = f.kind === "defect";
  // Untyped damage starts on the AI's best guess when there is one; the engineer confirms or changes it.
  const startCode = f.type_code || f.type_guess || "";
  const [code, setCode] = useState(startCode);
  const [severity, setSeverity] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const sevRef = useRef<HTMLSelectElement>(null);
  const typeRef = useRef<HTMLSelectElement>(null);

  useEffect(() => { setCode(startCode); setSeverity(""); setQuantity(""); setNotes(""); setError(null); }, [f.id, startCode]);

  const m = useMutation({
    mutationFn: (action: "confirm" | "reclassify" | "reject") =>
      api<{ status: string }>(`/api/review/${encodeURIComponent(f.id)}/decision`, {
        method: "POST",
        json: { action, defect_code: action === "reclassify" ? code : null, severity: severity || null, quantity: quantity ? Number(quantity) : null, notes },
      }).then(() => action),
    onSuccess: (action) => {
      qc.invalidateQueries({ queryKey: ["review"] });
      qc.invalidateQueries({ queryKey: ["road", f.survey_uuid] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["finding", f.id] });
      onDone(f.id, action);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        toast({ text: e.message, tone: "error" });
        qc.invalidateQueries({ queryKey: ["review"] });
        onDone(f.id, "skipped");
        return;
      }
      setError(e instanceof Error ? e.message : "The decision could not be saved.");
    },
  });

  const untyped = isDefect && !f.type_code; // the AI saw damage but couldn't tell which kind: the engineer picks
  const guessName = catalogue?.defects.find((d) => d.defect_code === f.type_guess)?.defect_name;
  const reclassified = isDefect && code && code !== f.type_code;
  const confirm = () => {
    if (isDefect && !code) { setError("Choose a defect type: the AI couldn't tell which kind of damage this is."); typeRef.current?.focus(); return; }
    if (isDefect && !severity) { setError("Choose a severity before confirming."); sevRef.current?.focus(); return; }
    m.mutate(reclassified ? "reclassify" : "confirm");
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || e.ctrlKey || e.metaKey || e.altKey || m.isPending) return;
      if (e.key === "c") { e.preventDefault(); confirm(); }
      if (e.key === "x") { e.preventDefault(); m.mutate("reject"); }
      if (e.key === "r" && isDefect) { e.preventDefault(); typeRef.current?.focus(); }
      if (["1", "2", "3"].includes(e.key) && isDefect) { e.preventDefault(); setSeverity(["Low", "Medium", "High"][Number(e.key) - 1]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <form className="decision" onSubmit={(e) => { e.preventDefault(); confirm(); }}>
      <div className="form-grid">
        {isDefect ? (
          <Field label="Defect type" htmlFor="dec-type"
                 hint={untyped ? (guessName && code === f.type_guess
                   ? `AI's best guess: ${guessName}, ${Math.round((f.type_guess_score ?? 0) * 100)}% sure. Change it if wrong (R).`
                   : "The AI couldn't tell the type. Choose one (R).") : reclassified ? `Reclassified from ${f.type_code} ${f.type_name}` : "Change this to reclassify (R)."}>
            <select id="dec-type" ref={typeRef} className="select" value={code} onChange={(e) => setCode(e.target.value)} aria-invalid={!!error && !code}>
              {untyped ? <option value="">Choose a type…</option>
                : !catalogue?.defects.some((d) => d.defect_code === f.type_code) ? <option value={f.type_code}>{f.type_code} {f.type_name}</option> : null}
              {catalogue?.defects.map((d) => <option key={d.defect_code} value={d.defect_code}>{d.defect_code} {d.defect_name}</option>)}
            </select>
          </Field>
        ) : null}
        {isDefect ? (
          <Field label="Severity" htmlFor="dec-sev" hint="Keys 1, 2, 3">
            <select id="dec-sev" ref={sevRef} className="select" value={severity} onChange={(e) => setSeverity(e.target.value)} aria-invalid={!!error && !severity}>
              <option value="">Choose…</option>
              {["Low", "Medium", "High"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
        ) : null}
        {isDefect ? (
          <Field label="Quantity (optional)" htmlFor="dec-qty" hint={catalogue?.defects.find((d) => d.defect_code === code)?.measurement_unit ?? undefined}>
            <input id="dec-qty" className="input" type="number" min="0" step="0.1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
        ) : null}
      </div>
      <Field label={isDefect ? "Notes" : "Notes or reason"} htmlFor="dec-notes">
        <textarea id="dec-notes" className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={isDefect ? "What you see, extent, anything the next person needs." : "For example: billboard, not a road sign."} />
      </Field>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="decision-actions">
        <button type="submit" className="btn btn-good" disabled={m.isPending}>{reclassified && !untyped ? "Reclassify and confirm" : "Confirm"} <span className="kbd">C</span></button>
        <button type="button" className="btn btn-danger" disabled={m.isPending} onClick={() => m.mutate("reject")}>Reject <span className="kbd">X</span></button>
      </div>
    </form>
  );
}

export function ReviewPage() {
  const { findingId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const survey = params.get("survey") || "";
  const kind = params.get("kind") || "all";
  const includeLow = params.get("low") === "1";
  const sort = params.get("sort") || "confidence";
  const qs = params.toString();

  const queue = useQuery({
    queryKey: ["review", "queue", kind, includeLow, sort],
    queryFn: () => api<{ items: (Finding & { road_code: string; road_name: string })[] }>(`/api/review/queue?kind=${kind}&include_low=${includeLow}&sort=${sort}`),
  });
  const catalogue = useQuery({ queryKey: ["review", "catalogue"], queryFn: () => api<Catalogue>("/api/review/catalogue"), staleTime: 10 * 60_000 });

  const all = queue.data?.items ?? [];
  const items = useMemo(() => all.filter((i) => !survey || i.survey_uuid === survey), [all, survey]);
  const roads = useMemo(() => uniq(all.map((i) => `${i.survey_uuid}|${i.road_code} ${i.road_name}`)), [all]);
  const current = findingId ? decodeURIComponent(findingId) : items[0]?.id;
  const idx = items.findIndex((i) => i.id === current);

  const detail = useQuery({
    queryKey: ["finding", current],
    queryFn: () => api<FindingDetail>(`/api/findings/${encodeURIComponent(current!)}`),
    enabled: !!current,
  });

  // ← / → move through the queue; the next finding and its image load in the background.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement).tagName) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (document.querySelector("dialog[open]")) return;
      if (e.key === "ArrowLeft" && idx > 0) { e.preventDefault(); goTo(items[idx - 1].id); }
      if (e.key === "ArrowRight" && idx >= 0 && idx < items.length - 1) { e.preventDefault(); goTo(items[idx + 1].id); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const next = items[idx + 1];
  useEffect(() => {
    if (!next) return;
    qc.prefetchQuery({ queryKey: ["finding", next.id], queryFn: () => api<FindingDetail>(`/api/findings/${encodeURIComponent(next.id)}`) });
    if (next.frame_uuid) new Image().src = imageUrl(next.frame_uuid);
  }, [next?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const goTo = (id: string | undefined) => nav(id ? `/review/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}` : `/review${qs ? `?${qs}` : ""}`);

  const onDone = (id: string, action: string) => {
    const next = items[idx + 1] ?? items[idx - 1];
    goTo(next && next.id !== id ? next.id : undefined);
    if (action === "skipped") return;
    const label = action === "reject" ? "Rejected" : action === "reclassify" ? "Reclassified and confirmed" : "Confirmed";
    toast({
      text: `${label}.`,
      action: {
        label: "Undo",
        run: async () => {
          try {
            await api(`/api/review/${encodeURIComponent(id)}/undo`, { method: "POST" });
            qc.invalidateQueries();
            goTo(id);
          } catch (e) {
            toast({ text: e instanceof Error ? e.message : "Undo failed.", tone: "error" });
          }
        },
      },
    }, 10_000);
  };

  return (
    <>
      <PageHeader
        title="Review queue"
        subtitle={queue.isSuccess ? `${items.length} AI finding${items.length === 1 ? "" : "s"} waiting${survey ? " on this road" : ""}` : "AI findings waiting for an engineer"}
      />
      <div className="filters" aria-label="Filter the queue">
        <Field label="Road" htmlFor="rq-road">
          <select id="rq-road" className="select" value={survey} onChange={(e) => setParam("survey", e.target.value)}>
            <option value="">All roads</option>
            {roads.map((r) => { const [id, label] = r.split("|"); return <option key={id} value={id}>{label}</option>; })}
          </select>
        </Field>
        <Field label="Type" htmlFor="rq-kind">
          <select id="rq-kind" className="select" value={kind} onChange={(e) => setParam("kind", e.target.value === "all" ? "" : e.target.value)}>
            <option value="all">Defects and assets</option><option value="defect">Road defects</option><option value="asset">Assets</option>
          </select>
        </Field>
        <Field label="Order" htmlFor="rq-sort">
          <select id="rq-sort" className="select" value={sort} onChange={(e) => setParam("sort", e.target.value === "confidence" ? "" : e.target.value)}>
            <option value="confidence">Most confident first</option><option value="chainage">Along the road</option>
          </select>
        </Field>
        <label className="switch" style={{ height: 36 }}>
          <input type="checkbox" checked={includeLow} onChange={(e) => setParam("low", e.target.checked ? "1" : "")} />Include low-confidence candidates
        </label>
      </div>

      {queue.isError ? <ErrorState error={queue.error} onRetry={() => queue.refetch()} /> : queue.isPending ? <SkeletonBlock rows={8} height={40} /> : !items.length ? (
        <Panel>
          <EmptyState title="All findings reviewed" action={<Link className="btn" to="/defects">Open the defects register</Link>}>
            {includeLow ? "Nothing is waiting." : "Nothing above the confidence threshold is waiting. Low-confidence candidates are hidden."}
          </EmptyState>
        </Panel>
      ) : (
        <div className="review">
          <Panel flush>
            <div className="review-list" role="list" aria-label="Findings waiting for review">
              {items.map((i) => (
                <Link key={i.id} role="listitem" to={`/review/${encodeURIComponent(i.id)}${qs ? `?${qs}` : ""}`} className={`queue-item${i.id === current ? " active" : ""}`} aria-current={i.id === current ? "true" : undefined}>
                  {i.frame_uuid ? <img src={imageUrl(i.frame_uuid, true)} alt="" loading="lazy" /> : <span className="ph" />}
                  <span style={{ minWidth: 0 }}>
                    <b>{i.type_name}</b>
                    <small>{i.road_code} · <span className="mono">{chainage(i.chainage_m)}</span>{i.model ? ` · ${i.model}` : null}</small>
                    <Confidence value={i.confidence} />
                  </span>
                </Link>
              ))}
            </div>
          </Panel>
          <Panel
            title={current ? <>{idx >= 0 ? `${idx + 1} of ${items.length}` : "Finding"}</> : "Finding"}
            id="rq-item"
            actions={
              <div className="row">
                <button className="btn btn-sm" disabled={idx <= 0} onClick={() => goTo(items[idx - 1]?.id)}>Previous</button>
                <button className="btn btn-sm" disabled={idx < 0 || idx >= items.length - 1} onClick={() => goTo(items[idx + 1]?.id)}>Skip</button>
              </div>
            }
          >
            <QueryState query={detail} skeleton={<SkeletonBlock rows={8} />}>
              {(d) => (
                <div className="split" style={{ alignItems: "start" }}>
                  <div className="stack" style={{ gap: 8 }}>
                    <EvidenceViewer evidence={d.evidence} label={d.finding.type_name} dashed />
                    <p className="muted" style={{ fontSize: 12 }}>
                      <span className="kbd">←</span> <span className="kbd">→</span> previous / next · hold <span className="kbd">B</span> to hide the box · click the image for full size
                    </p>
                  </div>
                  <div className="stack" style={{ gap: 16 }}>
                    <div>
                      <p className="muted" style={{ fontSize: 13 }}>AI suggests</p>
                      <h2 style={{ fontSize: 22 }}>{d.finding.type_name}</h2>
                      <div className="row" style={{ marginTop: 4 }}><Confidence value={d.finding.confidence} />{d.finding.band ? <span className="chip neutral">{d.finding.band}</span> : null}</div>
                    </div>
                    <dl className="kv">
                      <dt>Road</dt><dd><Link to={`/roads/${d.survey.survey_uuid}/findings?f=${encodeURIComponent(d.finding.id)}`}>{d.survey.road_code} — {d.survey.road_name}</Link></dd>
                      <dt>Chainage</dt><dd className="mono">{chainage(d.finding.chainage_m)}</dd>
                      <dt>Category</dt><dd>{d.finding.category}</dd>
                      {d.finding.model ? <><dt>Found by</dt><dd>{d.finding.model}</dd></> : null}
                    </dl>
                    {d.finding.status === "unreviewed" ? (
                      <Decision detail={d} catalogue={catalogue.data} onDone={onDone} />
                    ) : (
                      <p className="notice">This finding has already been reviewed by {d.finding.reviewed_by ?? "an engineer"}.</p>
                    )}
                  </div>
                </div>
              )}
            </QueryState>
          </Panel>
        </div>
      )}
    </>
  );
}
